import React, { useEffect, useState } from 'react';
import { spaceTypesAPI } from '../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Package, DollarSign, Edit, Upload, Plus } from 'lucide-react';
import { toast } from 'sonner';

export default function ProductsPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newProduct, setNewProduct] = useState({
    name: '',
    description: '',
    category: 'presencial',
    base_price_usd: 100,
    specs: {},
    art_requirements: {
      dimensions: '1920x1080',
      formats_allowed: ['pdf', 'jpg', 'png']
    },
    image_url: ''
  });

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      const response = await spaceTypesAPI.getAll();
      setProducts(response.data);
    } catch (error) {
      toast.error('Error al cargar productos');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setShowEditDialog(true);
  };

  const handleSaveEdit = async () => {
    try {
      await spaceTypesAPI.update(editingProduct.id, {
        name: editingProduct.name,
        description: editingProduct.description,
        category: editingProduct.category,
        base_price_usd: editingProduct.base_price_usd,
        specs: editingProduct.specs,
        art_requirements: editingProduct.art_requirements,
        image_url: editingProduct.image_url
      });
      toast.success('Producto actualizado');
      setShowEditDialog(false);
      loadProducts();
    } catch (error) {
      toast.error('Error al actualizar producto');
    }
  };

  const handleCreateProduct = async () => {
    if (!newProduct.name || !newProduct.description) {
      toast.error('Por favor completa los campos obligatorios');
      return;
    }
    
    setCreating(true);
    try {
      await spaceTypesAPI.create(newProduct);
      toast.success('Producto creado exitosamente');
      setShowCreateDialog(false);
      setNewProduct({
        name: '',
        description: '',
        category: 'presencial',
        base_price_usd: 100,
        specs: {},
        art_requirements: {
          dimensions: '1920x1080',
          formats_allowed: ['pdf', 'jpg', 'png']
        },
        image_url: ''
      });
      loadProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear producto');
    } finally {
      setCreating(false);
    }
  };

  const handleImportExcel = () => {
    toast.info('Función de importación Excel en desarrollo');
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>;
  }

  return (
    <div data-testid="products-page">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-slate-900 mb-2">Productos</h1>
          <p className="text-lg text-slate-600">Catálogo de productos publicitarios</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => setShowImportDialog(true)} data-testid="import-products-btn">
            <Upload className="w-4 h-4 mr-2" />
            Importar Excel
          </Button>
          <Button onClick={() => setShowCreateDialog(true)} data-testid="create-product-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Producto
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {products.map((product) => (
          <Card key={product.id} className="border-slate-200 hover:shadow-md transition-shadow" data-testid={`product-card-${product.id}`}>
            <CardHeader>
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3 flex-1">
                  {product.image_url ? (
                    <img 
                      src={product.image_url} 
                      alt={product.name}
                      className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
                    />
                  ) : (
                    <div className="w-16 h-16 bg-accent/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Package className="w-8 h-8 text-accent" />
                    </div>
                  )}
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <CardTitle className="text-lg">{product.name}</CardTitle>
                      <Badge 
                        variant={product.category === 'digital' ? 'default' : 'secondary'}
                        className="text-xs"
                      >
                        {product.category === 'digital' ? '📱 Digital' : '🏪 Presencial'}
                      </Badge>
                    </div>
                    <CardDescription>{product.description}</CardDescription>
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="flex items-center gap-1 text-2xl font-bold text-primary">
                    <DollarSign className="w-5 h-5" />
                    {product.base_price_usd}
                  </div>
                  <p className="text-xs text-slate-500">por mes</p>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {product.specs && Object.keys(product.specs).length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-slate-700 mb-2">Especificaciones:</p>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(product.specs).map(([key, value]) => (
                        <Badge key={key} variant="outline" className="text-xs">
                          {key}: {value}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                {product.art_requirements && (
                  <div>
                    <p className="text-sm font-medium text-slate-700 mb-1">Requisitos de arte:</p>
                    <p className="text-xs text-slate-600">
                      Dimensiones: {product.art_requirements.dimensions || 'N/A'}
                    </p>
                    <p className="text-xs text-slate-600">
                      Formatos: {product.art_requirements.formats_allowed?.join(', ') || 'N/A'}
                    </p>
                  </div>
                )}
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full"
                  onClick={() => handleEdit(product)}
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Editar
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Producto</DialogTitle>
            <DialogDescription>Actualiza la información del producto</DialogDescription>
          </DialogHeader>
          {editingProduct && (
            <div className="space-y-4">
              <div>
                <Label>Nombre</Label>
                <Input
                  value={editingProduct.name}
                  onChange={(e) => setEditingProduct({...editingProduct, name: e.target.value})}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Descripción</Label>
                <Textarea
                  value={editingProduct.description}
                  onChange={(e) => setEditingProduct({...editingProduct, description: e.target.value})}
                  rows={2}
                  className="mt-1"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Categoría</Label>
                  <Select
                    value={editingProduct.category}
                    onValueChange={(val) => setEditingProduct({...editingProduct, category: val})}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="presencial">🏪 Presencial</SelectItem>
                      <SelectItem value="digital">📱 Digital</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Precio (USD/mes)</Label>
                  <Input
                    type="number"
                    value={editingProduct.base_price_usd}
                    onChange={(e) => setEditingProduct({...editingProduct, base_price_usd: parseFloat(e.target.value)})}
                    className="mt-1"
                  />
                </div>
              </div>
              <div>
                <Label>URL de Imagen</Label>
                <Input
                  value={editingProduct.image_url || ''}
                  onChange={(e) => setEditingProduct({...editingProduct, image_url: e.target.value})}
                  placeholder="https://..."
                  className="mt-1"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <Button onClick={handleSaveEdit}>Guardar Cambios</Button>
                <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancelar</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Importar Productos desde Excel</DialogTitle>
            <DialogDescription>Sube un archivo Excel con los datos de los productos</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Archivo Excel</Label>
              <Input type="file" accept=".xlsx,.xls" className="mt-1" />
            </div>
            <p className="text-sm text-slate-600">
              El archivo debe contener las columnas: nombre, descripción, categoría (digital/presencial), precio, imagen_url
            </p>
            <div className="flex gap-3">
              <Button onClick={handleImportExcel}>Importar</Button>
              <Button variant="outline" onClick={() => setShowImportDialog(false)}>Cancelar</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Product Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nuevo Producto</DialogTitle>
            <DialogDescription>Crea un nuevo producto publicitario</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nombre *</Label>
              <Input
                value={newProduct.name}
                onChange={(e) => setNewProduct({...newProduct, name: e.target.value})}
                placeholder="Banner Vitrina"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Descripción *</Label>
              <Textarea
                value={newProduct.description}
                onChange={(e) => setNewProduct({...newProduct, description: e.target.value})}
                placeholder="Espacio publicitario en la vitrina principal..."
                rows={2}
                className="mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Categoría</Label>
                <Select
                  value={newProduct.category}
                  onValueChange={(val) => setNewProduct({...newProduct, category: val})}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="presencial">🏪 Presencial</SelectItem>
                    <SelectItem value="digital">📱 Digital</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Precio (USD/mes)</Label>
                <Input
                  type="number"
                  min="0"
                  value={newProduct.base_price_usd}
                  onChange={(e) => setNewProduct({...newProduct, base_price_usd: parseFloat(e.target.value) || 0})}
                  className="mt-1"
                />
              </div>
            </div>
            <div>
              <Label>URL de Imagen</Label>
              <Input
                value={newProduct.image_url}
                onChange={(e) => setNewProduct({...newProduct, image_url: e.target.value})}
                placeholder="https://..."
                className="mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Dimensiones del Arte</Label>
                <Input
                  value={newProduct.art_requirements.dimensions}
                  onChange={(e) => setNewProduct({
                    ...newProduct, 
                    art_requirements: {...newProduct.art_requirements, dimensions: e.target.value}
                  })}
                  placeholder="1920x1080"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Formatos Permitidos</Label>
                <Input
                  value={newProduct.art_requirements.formats_allowed?.join(', ')}
                  onChange={(e) => setNewProduct({
                    ...newProduct, 
                    art_requirements: {
                      ...newProduct.art_requirements, 
                      formats_allowed: e.target.value.split(',').map(f => f.trim())
                    }
                  })}
                  placeholder="pdf, jpg, png"
                  className="mt-1"
                />
              </div>
            </div>
            <div className="flex gap-3 pt-4">
              <Button onClick={handleCreateProduct} disabled={creating}>
                {creating ? 'Creando...' : 'Crear Producto'}
              </Button>
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                Cancelar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
